import { Link } from "react-router-dom";
import { Flower, ArrowLeft } from "@phosphor-icons/react";
import SiteFooter from "@/components/SiteFooter";
import usePageMeta from "@/lib/usePageMeta";

const YEAR = new Date().getFullYear();

const SECTIONS = [
  {
    id: "acceptance",
    h: "1. Acceptance of Terms",
    body: [
      "By creating an account, accessing, or using Wingwomen (the \"Platform\"), you agree to be bound by these Terms of Service (\"Terms\") and our Privacy Policy. If you do not agree, do not use the Platform.",
      "We may update these Terms from time to time. Material changes will be announced in-app. Continued use after a change means you accept the updated Terms.",
    ],
  },
  {
    id: "what",
    h: "2. What Wingwomen Is (and Isn't)",
    body: [
      "Wingwomen is a peer support platform that leverages artificial intelligence to connect users navigating reproductive-health experiences (including but not limited to endometriosis, PMOS, IVF, pregnancy, pregnancy loss, postpartum, fibroids, and menopause).",
      "Wingwomen is NOT a medical provider, a hospital, an insurance company, or a substitute for professional medical, mental-health, or legal advice. No doctor–patient relationship is created by using the Platform.",
    ],
  },
  {
    id: "ai-disclosure",
    h: "3. AI Disclosure",
    body: [
      "The AI Health Navigator, community summaries, and safety scans on the Platform are powered by large language models operated by third parties, currently Anthropic's Claude Sonnet 4.5.",
      "AI-generated content may be inaccurate, incomplete, biased, or out of date. It is provided for general informational and peer-support purposes only. You should independently verify any AI-generated statement before relying on it and should consult a licensed clinician about your specific situation.",
    ],
  },
  {
    id: "medical",
    h: "4. Medical Disclaimer & No Liability",
    body: [
      "Content on the Platform, whether generated by AI, posted by another user (a \"Peer\"), summarized from community activity, or shared by any third-party provider, is provided for informational and peer-support purposes only and is NOT medical advice, diagnosis, or treatment.",
      "To the maximum extent permitted by law, Wingwomen and its operators, employees, contractors, and affiliates disclaim any and all liability for any information obtained on, given on, or shared through the Platform, and for any decision made or action taken (or not taken) in reliance on that information.",
      "In a medical emergency, call your local emergency number or go to the nearest emergency department. Do not rely on the Platform for urgent care.",
    ],
  },
  {
    id: "legal",
    h: "5. Legal Disclaimer, Recommendations & Referrals",
    body: [
      "Nothing on the Platform should be interpreted as a recommendation about how, when, whether, or from whom you should receive medical treatment, therapy, medication, procedures, or any other form of care.",
      "Peer stories, tips, and referrals are the personal views and experiences of the individual posting them. Any provider, clinic, or specialist mentioned on the Platform (whether by a Peer, an AI response, or otherwise) is not endorsed, vetted, or credentialed by Wingwomen.",
      "Wingwomen is not responsible for any recommendation, favorable or unfavorable, made by any Peer or by any third-party provider on or through the Platform. You are solely responsible for your healthcare decisions and for evaluating the qualifications of any provider you choose to see.",
    ],
  },
  {
    id: "eligibility",
    h: "6. Eligibility",
    body: [
      "You must be at least 18 years old (or the age of majority in your jurisdiction) to use the Platform. By using Wingwomen you represent that you meet this requirement.",
      "One account per person. Anonymous usernames are required by default to protect your privacy.",
    ],
  },
  {
    id: "community",
    h: "7. Community Rules",
    body: [
      "Wingwomen is a safe space. When posting or messaging you agree not to: (a) share personally identifiable information about yourself or others (email, phone, address, full name, exact appointment details); (b) share unlawful, harassing, threatening, hateful, or sexually explicit content; (c) impersonate any person or provider; (d) advertise, promote, or sell products, services, or clinical trials without prior written consent; (e) attempt to extract, scrape, or resell Platform data.",
      "Our AI safety layer automatically flags and may block posts containing personal identifiers or harmful language. Repeat violations may result in account suspension or termination.",
    ],
  },
  {
    id: "privacy",
    h: "8. Privacy, HIPAA, and Data",
    body: [
      "Wingwomen is designed with HIPAA-aligned principles: pseudonymous usernames, encryption in transit, role-based access, audit logging, and data minimization. See our Privacy Policy for details on what we collect, how we use it, and your rights.",
      "You control what you share. Anything you post to a public timeline or condition hub is visible to other users; anything you send as a disappearing message is deleted from our servers on the schedule you selected.",
    ],
  },
  {
    id: "content",
    h: "9. Your Content",
    body: [
      "You retain ownership of the content you post. By posting, you grant Wingwomen a worldwide, royalty-free, non-exclusive license to host, display, moderate, and (with de-identification) analyze that content to operate and improve the Platform.",
      "You represent that you have the right to post any content you share and that it does not violate any third-party right or applicable law.",
    ],
  },
  {
    id: "ip",
    h: "10. Intellectual Property & Ownership",
    body: [
      "The Platform is owned and operated by Wingwomen, Inc., a venture-backed company founded in 2021. Wingwomen, Inc. received pre-seed investment from StartUp Health in 2022 and the Jumpstart Foundry accelerator in 2023. All references to \"Wingwomen\", \"Wingwomen\", or the Platform in these Terms refer to Wingwomen, Inc.",
      "All rights, title, and interest in and to the Platform, including but not limited to the Wingwomen name, brand, logo, tagline, editorial voice, similarity-score matching algorithm, condition hub taxonomy, loss-aware AI system prompts, product concepts, user-interface designs, and all associated software and code, are and shall remain the exclusive property of Wingwomen, Inc. and its licensors. Wingwomen™, Wingwomen™, and the flower mark are trademarks of Wingwomen, Inc.",
      "Except for the limited license granted to you to access and use the Platform in accordance with these Terms, no right, title, or interest in the Platform, or in any of its content, brands, or intellectual property, is transferred or licensed to you. You may not copy, reproduce, adapt, translate, distribute, publicly display, create derivative works of, decompile, reverse-engineer, or otherwise exploit any part of the Platform without prior written consent from Wingwomen, Inc.",
      "Third-party names, logos, and marks referenced on the Platform (including Anthropic, Claude, ElevenLabs, Stripe, Twilio, and others) are the property of their respective owners and are used for identification only, no endorsement or affiliation is implied. For licensing or partnership inquiries, contact hi@mywingwomen.com.",
    ],
  },
  {
    id: "termination",
    h: "11. Suspension & Termination",
    body: [
      "We may suspend or terminate your account at any time, with or without notice, for any violation of these Terms or for conduct we believe harms the community. You may delete your account at any time; disappearing messages are already ephemeral, and your profile can be permanently removed on request.",
    ],
  },
  {
    id: "warranty",
    h: "12. No Warranty",
    body: [
      "The Platform is provided \"as is\" and \"as available\" without warranty of any kind, express or implied, including merchantability, fitness for a particular purpose, non-infringement, or accuracy. We do not warrant that the Platform will be uninterrupted, error-free, or secure.",
    ],
  },
  {
    id: "liability",
    h: "13. Limitation of Liability",
    body: [
      "To the fullest extent permitted by law, Wingwomen shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, data, or goodwill, arising from your use of or inability to use the Platform, or from any content on the Platform, whether based on contract, tort, negligence, strict liability, or any other theory.",
    ],
  },
  {
    id: "indemnity",
    h: "14. Indemnification",
    body: [
      "You agree to indemnify and hold harmless Wingwomen and its operators from any claim, demand, loss, or damages, including reasonable attorneys' fees, arising out of or related to your content, your use of the Platform, or your violation of these Terms.",
    ],
  },
  {
    id: "law",
    h: "15. Governing Law & Disputes",
    body: [
      "These Terms are governed by the laws of the State of Delaware, USA, without regard to conflict-of-laws principles. Any dispute will be resolved by binding arbitration on an individual basis in accordance with the American Arbitration Association's rules. You waive any right to a class action.",
    ],
  },
  {
    id: "contact",
    h: "16. Contact",
    body: [
      "Questions about these Terms? Reach us at hi@mywingwomen.com.",
    ],
  },
];

export default function Terms() {
  usePageMeta({ title: "Terms of Service", description: "Wingwomen Terms of Service, AI disclosure, medical disclaimer, and legal disclaimers." });
  return (
    <div className="min-h-screen bg-cream text-ink">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="terms-brand-link" className="flex items-center gap-2">
            <Flower size={22} weight="duotone" className="text-coral-700" />
            <span className="font-display text-xl">Wingwomen</span>
          </Link>
          <Link to="/" className="text-sm hover:text-coral-700 flex items-center gap-1.5">
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4"><span className="rule" />Legal</div>
        <h1 className="font-display text-5xl leading-tight" data-testid="terms-title">Terms of Service</h1>
        <p className="mt-3 text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>

        <div className="mt-10 p-5 bg-coral-50 border border-coral-100 rounded-2xl text-sm text-ink/85 leading-relaxed">
          <strong>Please read carefully.</strong> Wingwomen is a peer support platform that
          uses AI to connect patients around their reproductive health. It is not a medical
          provider and does not give medical advice. By using the Platform you agree to the
          disclaimers and terms below.
        </div>

        <div className="mt-10 grid md:grid-cols-4 gap-x-8">
          <nav className="md:col-span-1 md:sticky md:top-24 h-fit space-y-1.5 mb-8 md:mb-0 text-xs" aria-label="Sections">
            {SECTIONS.map(s => (
              <a key={s.id} href={`#${s.id}`}
                 className="block text-muted-foreground hover:text-coral-700 leading-relaxed">
                {s.h}
              </a>
            ))}
          </nav>
          <div className="md:col-span-3 space-y-10">
            {SECTIONS.map(s => (
              <section key={s.id} id={s.id} className="scroll-mt-24">
                <h2 className="font-display text-2xl leading-snug">{s.h}</h2>
                <div className="mt-3 space-y-3 text-ink/85 leading-relaxed text-[15px]">
                  {s.body.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>

      <SiteFooter compact />
    </div>
  );
}
